import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import AllAthleteCard from './AllAthleteCard';
import { fetchAthletesByCondition, fetchAthletesSportTabs, clearAthletes } from '../../../store/actions/businessActions';
import { PageHeader, Card, Btn, Badge, SearchBox, Select, Input, Chip, Loading, EmptyState } from '../../ForBrands/ui/BrandUI';
import { Icon } from '../../ForBrands/ui/icons';
import Pagination from '../../ForBrands/ui/Pagination';
import '../../ForBrands/brandMode.css';

const PAGE_SIZE = 20;
const ALLOWED_SPORTS = ['All Players', 'Football', 'Basketball'];

// Card density glyphs for the phone toggle, drawn like the V2 icon set
// (24px grid, round caps, currentColor).
const DensityGlyph = ({ grid }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {grid ? (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </>
    ) : (
      <>
        <line x1="4" y1="6" x2="20" y2="6" />
        <line x1="4" y1="12" x2="20" y2="12" />
        <line x1="4" y1="18" x2="20" y2="18" />
      </>
    )}
  </svg>
);

// The signed-in Player Index ("/" and /business/dashboard, inside
// PlayerIndexLayout): sport, search and filters, the card grid and the pager.
const AllAthletes = () => {
  const [selectedSport, setSelectedSport] = useState('All Players');
  const [page, setPage] = useState(1);
  const [showScrollTop, setShowScrollTop] = useState(false);
  // Mobile-only card density toggle. Defaults to 1-per-row (fullest detail)
  // but user can flip to 2-per-row for a denser overview. Persisted so the
  // preference survives reloads.
  const [mobileCols, setMobileCols] = useState(() => {
    try {
      return localStorage.getItem('dashboardMobileCols') === '2' ? 2 : 1;
    } catch {
      return 1;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('dashboardMobileCols', String(mobileCols));
    } catch {
      /* ignore quota / unavailable storage */
    }
  }, [mobileCols]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const emptyArray = useMemo(() => [], []);
  const entitlement = useSelector((state) => state.entitlement?.entitlement);

  const codes = useSelector((state) => state.codes?.allCodes);
  const rawSportTabs = useSelector((state) => state.business?.sportTabs ?? emptyArray);
  const sportTabs = rawSportTabs.filter((sport) => ALLOWED_SPORTS.includes(sport));
  const athletes = useSelector((state) => state.business?.data ?? emptyArray);
  const totalCounts = useSelector((state) => state.business?.totalCounts ?? 0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({});
  // True until the first page answers, so the empty state never flashes.
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState('');

  // Check if user has free coach tier (for UI display only)
  const isCoachFree = entitlement === 'coach_free';

  useEffect(() => {
    dispatch(fetchAthletesSportTabs());
  }, [dispatch]);

  const fetchData = async (pageNumber, overrideSearch, overrideFilters) => {
    setIsLoading(true);
    await dispatch(clearAthletes());
    await dispatch(fetchAthletesByCondition({
      searchFields: JSON.stringify(overrideFilters !== undefined ? overrideFilters : filters),
      search: overrideSearch !== undefined ? overrideSearch : searchText,
      sport: selectedSport,
      page: pageNumber,
      limit: PAGE_SIZE,
    }));
    setIsLoading(false);
  };

  useEffect(() => {
    dispatch(clearAthletes());
    setPage(1);
    setSearchText('');
    fetchData(1, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSport]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const athleteCardClicked = (athlete) => {
    localStorage.setItem('athleteEmail', athlete.email);
    navigate(`/business/athlette/profile/${athlete.athleteId || ''}`);
  };

  const pageCount = Math.ceil(totalCounts / PAGE_SIZE);

  const goToPage = (next) => {
    if (next < 1 || next > pageCount) return;
    setPage(next);
    fetchData(next);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({});
    setSearchText('');
  };

  const handleClearSearch = () => {
    setSearchText('');
    setPage(1);
    fetchData(1, '');
  };

  // Prepare filterGroups from codes
  const getCodeOptions = useCallback((type, shouldSort = false) => {
    const options = codes
      .filter(c => c.type === type)
      .map(c => ({ value: c.value, label: c.name }));

    return shouldSort
      ? options.sort((a, b) => a.label.localeCompare(b.label))
      : options;
  }, [codes]);

  const baseFilterConfig = useMemo(() => [
    { label: 'First Name', name: 'name', type: 'input' },
    { label: 'Last name', name: 'surname', type: 'input' },
    { label: 'Cachescore', name: 'cacheScore', type: 'input', inputType: 'number' },
    { label: 'Gender', name: 'gender', type: 'select', codeType: 'gender' },
    { label: 'State', name: 'state', type: 'select', codeType: 'state', sorted: true },
    { label: 'City', name: 'city', type: 'input' },
    { label: 'School name', name: 'schoolName', type: 'input' },
  ], []);

  const filterGroups = useMemo(() => {
    if (!codes) return [];

    return baseFilterConfig.map(filter => {
      if (filter.type === 'input') {
        return filter;
      }
      return {
        ...filter,
        options: getCodeOptions(filter.codeType, filter.sorted)
      };
    });
  }, [codes, baseFilterConfig, getCodeOptions]);

  if (isLoading) {
    return <Loading label="Loading athletes..." />;
  }

  const activeFilterCount = Object.keys(filters).filter((k) => filters[k]).length;
  const runSearch = () => { setPage(1); fetchData(1); };
  const clearEverything = () => { handleClearFilters(); setPage(1); fetchData(1, '', {}); };
  const removeFilter = (key) => {
    const next = { ...filters };
    delete next[key];
    setFilters(next);
    setPage(1);
    fetchData(1, undefined, next);
  };
  const filterDisplay = (key, value) => {
    const config = baseFilterConfig.find((f) => f.name === key);
    const label = config?.label || key;
    let displayValue = value;
    if (config?.type === 'select') {
      const opt = filterGroups.find((f) => f.name === key)?.options?.find((o) => String(o.value) === String(value));
      if (opt) displayValue = opt.label;
    }
    return `${label}: ${displayValue}`;
  };

  return (
    <div className="w-full min-w-0 max-w-full">
      <PageHeader
        eyebrow="Athletes"
        title="Player index"
        subtitle={`${Number(totalCounts || 0).toLocaleString()} athletes across every sport, level and school on CACHE.`}
      />

      {/* Sport, search and filters */}
      <Card padding="p-5" className="mb-4 w-full min-w-0 max-w-full">
        <form className="flex flex-wrap items-center gap-3" onSubmit={(e) => { e.preventDefault(); runSearch(); }}>
          <Select
            value={selectedSport}
            onChange={(e) => setSelectedSport(e.target.value)}
            aria-label="Sport"
            className="w-full sm:w-[200px]"
          >
            {sportTabs.map((sport) => (
              <option key={sport} value={sport}>
                {sport === 'All Players' ? 'All sports' : sport}
              </option>
            ))}
          </Select>
          <SearchBox
            aria-label="Search athletes"
            value={searchText}
            onChange={setSearchText}
            placeholder="Search by name, school, team or position, then press Enter"
            className="min-w-[240px] flex-1"
          />
          <Btn variant="secondary" type="submit">Search</Btn>
          <Btn variant={drawerOpen ? 'primary' : 'secondary'} onClick={() => setDrawerOpen(!drawerOpen)} aria-expanded={drawerOpen}>
            Filters
            {activeFilterCount > 0 && (
              <Badge tone={drawerOpen ? 'onDark' : 'brand'} className="ml-2">{activeFilterCount}</Badge>
            )}
          </Btn>
        </form>

        {drawerOpen && (
          <div className="mt-4 border-t border-bm-border pt-4">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {filterGroups.map((filter) => (
                filter.type === 'input' ? (
                  <Input
                    key={filter.name}
                    type={filter.inputType || 'text'}
                    value={filters[filter.name] || ''}
                    onChange={(e) => setFilters({ ...filters, [filter.name]: e.target.value || undefined })}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
                    placeholder={filter.label}
                    aria-label={filter.label}
                  />
                ) : (
                  <Select
                    key={filter.name}
                    value={filters[filter.name] || ''}
                    onChange={(e) => setFilters({ ...filters, [filter.name]: e.target.value || undefined })}
                    aria-label={filter.label}
                  >
                    <option value="">{filter.label}</option>
                    {(filter.options || []).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Select>
                )
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Btn onClick={runSearch}>Apply filters</Btn>
              {activeFilterCount > 0 && (
                <Btn variant="link" onClick={clearEverything}>Clear all</Btn>
              )}
            </div>
          </div>
        )}

        {(activeFilterCount > 0 || searchText) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="bm-label-xs text-bm-tertiary">Active</span>
            {searchText && (
              <Chip active onClick={handleClearSearch} title="Remove this search">
                Search: "{searchText}" &times;
              </Chip>
            )}
            {Object.entries(filters).map(([key, value]) => (value ? (
              <Chip key={key} active onClick={() => removeFilter(key)} title="Remove this filter">
                {filterDisplay(key, value)} &times;
              </Chip>
            ) : null))}
            <Btn variant="link" size="sm" onClick={clearEverything}>Clear all</Btn>
          </div>
        )}
      </Card>

      {/* Mobile card-density toggle */}
      <div className="mb-2 mt-4 flex items-center justify-end lg:hidden">
        <button
          type="button"
          onClick={() => setMobileCols(mobileCols === 1 ? 2 : 1)}
          aria-label={mobileCols === 1 ? 'Switch to 2 cards per row' : 'Switch to 1 card per row'}
          title={mobileCols === 1 ? '2 per row' : '1 per row'}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-bm-border bg-white text-bm-muted transition-colors hover:bg-bm-subtle hover:text-bm-ink"
        >
          <DensityGlyph grid={mobileCols === 1} />
        </button>
      </div>

      {/* Card grid */}
      <div className="mt-6 w-full lg:mt-12">
        {athletes.length === 0 ? (
          <EmptyState
            action={(activeFilterCount > 0 || searchText) && (
              <Btn variant="secondary" onClick={clearEverything}>Clear filters</Btn>
            )}
          >
            No athletes found.
          </EmptyState>
        ) : (
          <div
            className={`grid ${
              mobileCols === 2 ? 'grid-cols-2' : 'grid-cols-1'
            } sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6`}
          >
            {athletes.map((athlete) => (
              <div key={athlete.email}>
                <AllAthleteCard
                  athlete={athlete}
                  codes={codes}
                  detailRequired={true}
                  onClick={() => athleteCardClicked(athlete)}
                  isHoverRequired={true}
                  showLock={isCoachFree}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {pageCount > 0 && (
        <Pagination page={page} pageCount={pageCount} onChange={goToPage} className="my-10" />
      )}

      {showScrollTop && (
        <button
          type="button"
          onClick={scrollToTop}
          aria-label="Back to top"
          title="Back to top"
          className="fixed bottom-6 right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-bm-orange text-white shadow-xl transition-colors hover:bg-[#e63d00]"
        >
          <Icon name="arrow-left" size={18} className="rotate-90" />
        </button>
      )}
    </div>
  );
};

export default AllAthletes;
